import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  SaveStockAdjustmentRequest,
  StockAdjustmentFilterParams,
  StockAdjustmentLineRecord,
  StockAdjustmentRecord,
  StockAdjustmentSummaryCounts,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapAdjustmentLine = (
  v: Record<string, unknown>,
): StockAdjustmentLineRecord => ({
  id: String(v.id || ''),
  stock_adjustment_id: String(v.stock_adjustment_id || ''),
  inventory_item_id: String(v.inventory_item_id || ''),
  quantity_adjusted: toNumber(v.quantity_adjusted),
  unit_cost: v.unit_cost === null ? null : toNumber(v.unit_cost),
  reason: (v.reason as string | null) ?? null,
  created_by: (v.created_by as string | null) ?? null,
  created_at: String(v.created_at || ''),
});

const mapAdjustment = (v: Record<string, unknown>): StockAdjustmentRecord => ({
  id: String(v.id || ''),
  company_id: toNumber(v.company_id),
  outlet_id: String(v.outlet_id || ''),
  stock_location_id: String(v.stock_location_id || ''),
  adjustment_date: String(v.adjustment_date || ''),
  adjustment_type:
    v.adjustment_type as StockAdjustmentRecord['adjustment_type'],
  status: v.status as StockAdjustmentRecord['status'],
  reference: (v.reference as string | null) ?? null,
  notes: (v.notes as string | null) ?? null,
  journal_entry_id: (v.journal_entry_id as string | null) ?? null,
  posted_at: (v.posted_at as string | null) ?? null,
  posted_by: (v.posted_by as string | null) ?? null,
  created_by: (v.created_by as string | null) ?? null,
  created_at: String(v.created_at || ''),
  updated_at: String(v.updated_at || ''),
  lines: Array.isArray(v.inventory_stock_adjustment_lines)
    ? (v.inventory_stock_adjustment_lines as Record<string, unknown>[]).map(
        mapAdjustmentLine,
      )
    : undefined,
});

export async function fetchStockAdjustments(
  filters: StockAdjustmentFilterParams,
): Promise<PaginatedResponse<StockAdjustmentRecord>> {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('inventory_stock_adjustments')
    .select('*', { count: 'exact' })
    .eq('company_id', filters.companyId)
    .eq('outlet_id', filters.outletId)
    .order('adjustment_date', { ascending: false })
    .range(from, to);

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }
  if (filters.adjustmentType && filters.adjustmentType !== 'all') {
    query = query.eq('adjustment_type', filters.adjustmentType);
  }
  if (filters.dateFrom) query = query.gte('adjustment_date', filters.dateFrom);
  if (filters.dateTo) query = query.lte('adjustment_date', filters.dateTo);
  if (filters.search) {
    query = query.or(
      `reference.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`,
    );
  }

  const { data, error, count } = await query;
  if (error) throw error;

  const totalPages = Math.ceil((count ?? 0) / pageSize);
  return {
    data: (data ?? []).map((r) => mapAdjustment(r as Record<string, unknown>)),
    pagination: {
      page,
      pageSize,
      totalItems: count ?? 0,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    },
  };
}

export async function fetchStockAdjustmentDetail(
  adjustmentId: string,
): Promise<StockAdjustmentRecord | null> {
  const { data, error } = await supabase
    .from('inventory_stock_adjustments')
    .select('*, inventory_stock_adjustment_lines(*)')
    .eq('id', adjustmentId)
    .single();

  if (error) throw error;
  if (!data) return null;
  return mapAdjustment(data as Record<string, unknown>);
}

export async function fetchStockAdjustmentSummary(
  companyId: number,
  outletId: string,
): Promise<StockAdjustmentSummaryCounts> {
  const { data, error } = await supabase
    .from('inventory_stock_adjustments')
    .select('status, adjustment_type')
    .eq('company_id', companyId)
    .eq('outlet_id', outletId);

  if (error) throw error;

  const rows = data ?? [];
  return {
    totalAdjustments: rows.length,
    draftAdjustments: rows.filter((r) => r.status === 'draft').length,
    postedAdjustments: rows.filter((r) => r.status === 'posted').length,
    wastageAdjustments: rows.filter((r) => r.adjustment_type === 'wastage')
      .length,
  };
}

export async function saveStockAdjustment(
  req: SaveStockAdjustmentRequest,
): Promise<ApiResponse<StockAdjustmentRecord>> {
  const today = new Date().toISOString().split('T')[0];

  if (req.id) {
    const { data: existing } = await supabase
      .from('inventory_stock_adjustments')
      .select('status')
      .eq('id', req.id)
      .single();
    if (existing?.status !== 'draft') {
      return { success: false, error: 'Only draft adjustments can be edited' };
    }

    const { error: headerError } = await supabase
      .from('inventory_stock_adjustments')
      .update({
        stock_location_id: req.stockLocationId,
        adjustment_date: req.adjustmentDate ?? today,
        adjustment_type: req.adjustmentType,
        reference: req.reference ?? null,
        notes: req.notes ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', req.id);

    if (headerError) return { success: false, error: headerError.message };

    await supabase
      .from('inventory_stock_adjustment_lines')
      .delete()
      .eq('stock_adjustment_id', req.id);

    if (req.lines.length > 0) {
      const { error: linesError } = await supabase
        .from('inventory_stock_adjustment_lines')
        .insert(
          req.lines.map((l) => ({
            stock_adjustment_id: req.id,
            inventory_item_id: l.inventoryItemId,
            quantity_adjusted: l.quantityAdjusted,
            unit_cost: l.unitCost ?? null,
            reason: l.reason ?? null,
          })),
        );
      if (linesError) return { success: false, error: linesError.message };
    }

    const updated = await fetchStockAdjustmentDetail(req.id);
    return { success: true, data: updated ?? undefined };
  }

  const { data: header, error: headerError } = await supabase
    .from('inventory_stock_adjustments')
    .insert({
      company_id: req.companyId,
      outlet_id: req.outletId,
      stock_location_id: req.stockLocationId,
      adjustment_date: req.adjustmentDate ?? today,
      adjustment_type: req.adjustmentType,
      reference: req.reference ?? null,
      notes: req.notes ?? null,
    })
    .select()
    .single();

  if (headerError) return { success: false, error: headerError.message };

  const adjustmentId = (header as Record<string, unknown>).id as string;

  if (req.lines.length > 0) {
    const { error: linesError } = await supabase
      .from('inventory_stock_adjustment_lines')
      .insert(
        req.lines.map((l) => ({
          stock_adjustment_id: adjustmentId,
          inventory_item_id: l.inventoryItemId,
          quantity_adjusted: l.quantityAdjusted,
          unit_cost: l.unitCost ?? null,
          reason: l.reason ?? null,
        })),
      );
    if (linesError) return { success: false, error: linesError.message };
  }

  const created = await fetchStockAdjustmentDetail(adjustmentId);
  return { success: true, data: created ?? undefined };
}

export async function postStockAdjustment(adjustmentId: string): Promise<
  ApiResponse<{
    adjustmentId: string;
    linesProcessed: number;
    totalWastage: number;
    journalEntryId: string | null;
  }>
> {
  const { data, error } = await supabase.rpc('post_stock_adjustment', {
    p_adjustment_id: adjustmentId,
  });

  if (error) return { success: false, error: error.message };

  const result = data as Record<string, unknown>;
  if (!result.success) {
    return { success: false, error: String(result.error ?? 'Post failed') };
  }

  return {
    success: true,
    data: {
      adjustmentId: String(result.adjustment_id),
      linesProcessed: Number(result.lines_processed ?? 0),
      totalWastage: Number(result.total_wastage ?? 0),
      journalEntryId: (result.journal_entry_id as string | null) ?? null,
    },
  };
}
